import { friendlyCrmSyncError } from "@/lib/crmSyncError";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { showActionError, showActionErrorFromUnknown } from "@/lib/feedback";
import { Drawer } from "@/components/organisms/Drawer";
import { Badge } from "@/components/atoms/Badge";
import { Button } from "@/components/atoms/Button";
import { CopyButton } from "@/components/atoms/CopyButton";
import { Input } from "@/components/atoms/Input";
import { IconButton } from "@/components/atoms/IconButton";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import type { RecurrenceFreq } from "@/types/availability";
import { WeekdayPicker } from "@/modules/availability/WeekdayPicker";
import { diaryService } from "@/services/diaryService";
import { propertiesService } from "@/services/propertiesService";
import { contactsService } from "@/services/contactsService";
import { usersService, isNegotiatorUser } from "@/services/usersService";
import { contactDetailsToSummary } from "@/modules/contacts/contactFormUtils";
import type { ContactDetails } from "@/types/contact";
import type { ApiError } from "@/types/api";
import type {
  DiaryEntryDetail,
  ContactSummary,
  PropertyDetail,
  PropertySummary,
  UserSummary,
} from "@/types/entities";
import { useSettingsStore } from "@/store/settingsStore";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import {
  MapPin,
  Users,
  UserCircle,
  AlignLeft,
  Mail,
  Phone,
  Home,
  User,
  X,
  UserPlus,
  UserMinus,
  History,
} from "lucide-react";
import { clsx } from "clsx";
import { formatRelativeTime } from "@/lib/format";
import { propertyDisplayLabel } from "@/lib/propertyDisplay";
import { crmDisplayName } from "@/lib/syncAlto";
import {
  fromDatetimeLocalToApi,
  isBeforeToday,
  parseApiDateTime,
  toDatetimeLocalValue,
  todayDatetimeLocalMin,
} from "@/utils/date-helpers";
import {
  DIARY_CANCELLED_STATUS_VALUES,
  DIARY_STATUS_OPTIONS,
  diaryInternalNotesFromEntry,
  diaryIsCancelledForStatus,
  supportsDiaryNotesAndFeedback,
  isDiaryViewingType,
  isDiaryMeetingEntry,
  normalizeDiaryStatusValue,
} from "./diaryEntryUtils";
import { DiaryDateTimeField } from "./DiaryDateTimeField";
import { SyncStatus } from "@/components/molecules/SyncStatus";

const RECURRENCE_OPTIONS = [
  { value: "NONE", label: "Does not repeat" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
] as const;

interface Props {
  open: boolean;
  entryId: string | null;
  onClose: () => void;
  onSaved?: (entry?: DiaryEntryDetail) => void;
  /** For recurring events, the specific calendar day the user clicked on. */
  occurrenceDate?: Date | null;
  /**
   * Alto sync status from the list row. `GET /diary-entries/{id}` omits `syncAlto`,
   * so the status has to be handed down from `GET /diary-entries`.
   */
  crmSyncStatus?: string | null;
  syncAlto?: string | null;
  /**
   * Fired after a resync is queued. Must refetch `GET /diary-entries` — refetching
   * the detail here would be pointless, since that body carries no `syncAlto`.
   */
  onResynced?: () => void;
}

export function DiaryDetailDrawer({
  open,
  entryId,
  onClose,
  onSaved,
  occurrenceDate,
  crmSyncStatus,
  syncAlto,
  onResynced,
}: Props) {
  const { legalEntityId: contextLegalEntityId } = useSettingsStore();
  const [data, setData] = useState<DiaryEntryDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const fetchSeq = useRef(0);
  const [retryKey, setRetryKey] = useState(0);
  const [activeTab, setActiveTab] = useState<"property" | "feedback" | "notes">(
    "property",
  );
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [savingTime, setSavingTime] = useState(false);
  const [propertyDetailsById, setPropertyDetailsById] = useState<
    Record<string, PropertyDetail>
  >({});
  const [propertyDetailsLoading, setPropertyDetailsLoading] = useState(false);
  const propertyFetchSeq = useRef(0);
  const [contactSummariesById, setContactSummariesById] = useState<
    Record<string, ContactSummary>
  >({});
  const [contactDetailsLoading, setContactDetailsLoading] = useState(false);
  const contactFetchSeq = useRef(0);
  const [userSummariesById, setUserSummariesById] = useState<
    Record<string, UserSummary>
  >({});
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const userFetchSeq = useRef(0);
  const [negotiators, setNegotiators] = useState<UserSummary[]>([]);
  const [loadingNegotiators, setLoadingNegotiators] = useState(false);
  const negotiatorFetchSeq = useRef(0);
  const [editingNegotiator, setEditingNegotiator] = useState(false);
  const [selectedNegotiatorId, setSelectedNegotiatorId] = useState("");
  const [savingNegotiator, setSavingNegotiator] = useState(false);
  const [userPendingRemoval, setUserPendingRemoval] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const closeRemoveUserDialog = useCallback(() => {
    if (savingNegotiator) return;
    setUserPendingRemoval(null);
  }, [savingNegotiator]);
  const { onOverlayClick: onRemoveUserOverlayClick } = useModalDismiss(
    Boolean(userPendingRemoval) && !savingNegotiator,
    closeRemoveUserDialog,
  );
  const [selectedStatus, setSelectedStatus] = useState("ACTIVE");
  const [savingStatus, setSavingStatus] = useState(false);
  const [recurFreq, setRecurFreq] = useState<RecurrenceFreq>("NONE");
  const [recurInterval, setRecurInterval] = useState(1);
  const [recurUntil, setRecurUntil] = useState("");
  const [recurDays, setRecurDays] = useState<string[]>([]);
  const [recurExcluded, setRecurExcluded] = useState<string[]>([]);
  const [recurExDraft, setRecurExDraft] = useState("");
  const [savingRecurrence, setSavingRecurrence] = useState(false);

  useEffect(() => {
    if (!open || !data) {
      setRecurFreq("NONE");
      setRecurInterval(1);
      setRecurUntil("");
      setRecurDays([]);
      setRecurExcluded([]);
      setRecurExDraft("");
      return;
    }
    const t = (data.recurrenceType ?? "").trim().toUpperCase();
    setRecurFreq(data.recurring && (t === "DAILY" || t === "WEEKLY") ? (t as RecurrenceFreq) : "NONE");
    setRecurInterval(Math.max(1, data.recurrenceInterval ?? 1));
    const todayStr = new Date().toISOString().slice(0, 10);
    const loadedUntil = data.recurrenceUntil ? String(data.recurrenceUntil).slice(0, 10) : "";
    setRecurUntil(loadedUntil && loadedUntil < todayStr ? "" : loadedUntil);
    setRecurDays(data.recurrenceDaysOfWeek ?? []);
    setRecurExcluded((data.recurrenceExceptions ?? []).filter((d) => d >= todayStr));
    setRecurExDraft("");
  }, [open, data]);

  useEffect(() => {
    if (!open) {
      setData(null);
      setLoadError(null);
      setRetryKey(0);
      setActiveTab("property");
      setPropertyDetailsById({});
      setPropertyDetailsLoading(false);
      setContactSummariesById({});
      setContactDetailsLoading(false);
      setUserSummariesById({});
      setUserDetailsLoading(false);
      setUserPendingRemoval(null);
      return;
    }
    if (!entryId) {
      setData(null);
      setLoadError(null);
      setLoading(false);
      setPropertyDetailsById({});
      setPropertyDetailsLoading(false);
      setContactSummariesById({});
      setContactDetailsLoading(false);
      setUserSummariesById({});
      setUserDetailsLoading(false);
      setUserPendingRemoval(null);
      return;
    }

    const seq = ++fetchSeq.current;
    setLoading(true);
    setLoadError(null);

    diaryService
      .getOne(entryId)
      .then((detail) => {
        if (seq !== fetchSeq.current) return;
        setData(detail);
      })
      .catch((err: unknown) => {
        if (seq !== fetchSeq.current) return;
        const msg =
          err &&
          typeof err === "object" &&
          "message" in err &&
          typeof (err as ApiError).message === "string"
            ? (err as ApiError).message
            : "Failed to load diary entry";
        setData(null);
        setLoadError(msg);
        showActionError(msg, { title: "Failed to load diary entry" });
        console.error("Failed to load diary entry", err);
      })
      .finally(() => {
        if (seq !== fetchSeq.current) return;
        setLoading(false);
      });
  }, [open, entryId, retryKey]);

  useEffect(() => {
    if (!open || !entryId || !data?.startDate) {
      setStartLocal("");
      setEndLocal("");
      return;
    }
    if (occurrenceDate && data.recurring) {
      const startDt = parseApiDateTime(data.startDate);
      const endDt = data.endDate ? parseApiDateTime(data.endDate) : startDt;
      if (startDt && endDt) {
        const occStart = new Date(startDt);
        occStart.setFullYear(occurrenceDate.getFullYear(), occurrenceDate.getMonth(), occurrenceDate.getDate());
        const occEnd = new Date(endDt);
        occEnd.setFullYear(occurrenceDate.getFullYear(), occurrenceDate.getMonth(), occurrenceDate.getDate());
        setStartLocal(toDatetimeLocalValue(occStart));
        setEndLocal(toDatetimeLocalValue(occEnd));
        return;
      }
    }
    setStartLocal(toDatetimeLocalValue(data.startDate));
    setEndLocal(
      data.endDate
        ? toDatetimeLocalValue(data.endDate)
        : toDatetimeLocalValue(data.startDate),
    );
  }, [open, entryId, data, occurrenceDate]);

  useEffect(() => {
    if (!open || !entryId) {
      setPropertyDetailsById({});
      setPropertyDetailsLoading(false);
      return;
    }
    if (!data) {
      setPropertyDetailsById({});
      setPropertyDetailsLoading(false);
      return;
    }
    // Only skip as stale when API includes an id that clearly belongs to another entry.
    if (
      data.id != null &&
      String(data.id).trim() !== "" &&
      data.id !== entryId
    ) {
      setPropertyDetailsById({});
      setPropertyDetailsLoading(false);
      return;
    }
    if (!data.properties?.length) {
      setPropertyDetailsById({});
      setPropertyDetailsLoading(false);
      return;
    }

    const ids = [
      ...new Set(
        data.properties
          .map((p) => diaryLinkedPropertyAtlasId(p))
          .filter((id): id is string => Boolean(id)),
      ),
    ];
    if (!ids.length) {
      setPropertyDetailsById({});
      setPropertyDetailsLoading(false);
      return;
    }
    const seq = ++propertyFetchSeq.current;
    setPropertyDetailsLoading(true);

    void Promise.all(
      ids.map((id) =>
        propertiesService.getOne(id).then(
          (d) => [id, d] as const,
          () => [id, undefined] as const,
        ),
      ),
    ).then((pairs) => {
      if (seq !== propertyFetchSeq.current) return;
      const next: Record<string, PropertyDetail> = {};
      for (const [id, d] of pairs) {
        if (d) next[id] = d;
      }
      setPropertyDetailsById(next);
      setPropertyDetailsLoading(false);
    });
  }, [open, entryId, data]);

  useEffect(() => {
    if (!open) {
      negotiatorFetchSeq.current += 1;
      setNegotiators([]);
      setLoadingNegotiators(false);
      setEditingNegotiator(false);
      setSelectedNegotiatorId("");
      setSavingNegotiator(false);
      setSelectedStatus("ACTIVE");
      setSavingStatus(false);
      return;
    }
    if (!data) {
      setSelectedNegotiatorId("");
      setSelectedStatus("ACTIVE");
      return;
    }
    const currentNegotiator = data.users?.[0]?.id ?? "";
    setSelectedNegotiatorId(currentNegotiator);
    setSelectedStatus(normalizeDiaryStatusValue(data.status));
  }, [open, data]);

  useEffect(() => {
    if (!open || !entryId) {
      setContactSummariesById({});
      setContactDetailsLoading(false);
      return;
    }
    if (!data) {
      setContactSummariesById({});
      setContactDetailsLoading(false);
      return;
    }
    if (
      data.id != null &&
      String(data.id).trim() !== "" &&
      data.id !== entryId
    ) {
      setContactSummariesById({});
      setContactDetailsLoading(false);
      return;
    }
    if (!data.contacts?.length) {
      setContactSummariesById({});
      setContactDetailsLoading(false);
      return;
    }

    const ids = [
      ...new Set(
        data.contacts
          .map((c) => (typeof c.id === "string" ? c.id.trim() : ""))
          .filter((contactId): contactId is string => Boolean(contactId)),
      ),
    ];
    if (!ids.length) {
      setContactSummariesById({});
      setContactDetailsLoading(false);
      return;
    }

    const seq = ++contactFetchSeq.current;
    setContactDetailsLoading(true);

    void Promise.all(
      ids.map((id) =>
        contactsService.getOne(id).then(
          (raw) => {
            try {
              const summary = contactDetailsToSummary(raw as ContactDetails);
              return [id, summary] as const;
            } catch {
              return [id, undefined] as const;
            }
          },
          () => [id, undefined] as const,
        ),
      ),
    ).then((pairs) => {
      if (seq !== contactFetchSeq.current) return;
      const next: Record<string, ContactSummary> = {};
      for (const [contactIdKey, summary] of pairs) {
        if (summary) next[contactIdKey] = summary;
      }
      setContactSummariesById(next);
      setContactDetailsLoading(false);
    });
  }, [open, entryId, data]);

  useEffect(() => {
    if (!open || !entryId) {
      setUserSummariesById({});
      setUserDetailsLoading(false);
      return;
    }
    if (!data) {
      setUserSummariesById({});
      setUserDetailsLoading(false);
      return;
    }
    if (
      data.id != null &&
      String(data.id).trim() !== "" &&
      data.id !== entryId
    ) {
      setUserSummariesById({});
      setUserDetailsLoading(false);
      return;
    }
    if (!data.users?.length) {
      setUserSummariesById({});
      setUserDetailsLoading(false);
      return;
    }

    const ids = [
      ...new Set(
        data.users
          .map((u) => (typeof u.id === "string" ? u.id.trim() : ""))
          .filter((userId): userId is string => Boolean(userId)),
      ),
    ];
    if (!ids.length) {
      setUserSummariesById({});
      setUserDetailsLoading(false);
      return;
    }

    const seq = ++userFetchSeq.current;
    setUserDetailsLoading(true);

    void Promise.all(
      ids.map((id) =>
        usersService.getOne(id).then(
          (summary) => [id, summary] as const,
          () => [id, undefined] as const,
        ),
      ),
    ).then((pairs) => {
      if (seq !== userFetchSeq.current) return;
      const next: Record<string, UserSummary> = {};
      for (const [userIdKey, summary] of pairs) {
        if (summary) next[userIdKey] = summary;
      }
      setUserSummariesById(next);
      setUserDetailsLoading(false);
    });
  }, [open, entryId, data]);

  const fetchNegotiators = useCallback(
    (trimmedSearch: string) => {
      const seq = ++negotiatorFetchSeq.current;
      const q = trimmedSearch.trim();
      setLoadingNegotiators(true);
      usersService
        .listNegotiators(
          {
            ...(contextLegalEntityId
              ? { legalEntityId: contextLegalEntityId }
              : {}),
            ...(q ? { search: q } : {}),
          },
          { page: 0, size: 200 },
        )
        .then((res) => {
          if (seq !== negotiatorFetchSeq.current) return;
          setNegotiators(res.content ?? []);
        })
        .catch((err) => {
          if (seq !== negotiatorFetchSeq.current) return;
          console.error("Failed to load negotiators", err);
        })
        .finally(() => {
          if (seq !== negotiatorFetchSeq.current) return;
          setLoadingNegotiators(false);
        });
    },
    [contextLegalEntityId],
  );

  useEffect(() => {
    if (
      !open ||
      !editingNegotiator ||
      isDiaryViewingType(data?.type) ||
      data?.isCancelled
    ) {
      return;
    }
    fetchNegotiators("");
  }, [open, data, editingNegotiator, fetchNegotiators]);

  const combinedNegotiators = useMemo(() => {
    const map = new Map<string, UserSummary>();
    for (const u of data?.users ?? []) {
      if (u?.id) map.set(u.id, u);
    }
    for (const u of negotiators) {
      if (u?.id && isNegotiatorUser(u)) map.set(u.id, u);
    }
    return Array.from(map.values()).filter(
      (u) => isNegotiatorUser(u) || data?.users?.some((du) => du.id === u.id),
    );
  }, [data, negotiators]);

  const negotiatorOptions = useMemo(
    () =>
      combinedNegotiators.map((u) => {
        const primary =
          (u.name && u.name.trim()) ||
          (u.email && u.email.trim()) ||
          `(User ${u.externalId || "—"})`;
        const bits = [
          u.role && String(u.role).trim().replace(/_/g, " "),
        ].filter(Boolean);
        const suffix = bits.length ? ` — ${bits.join(" · ")}` : "";
        return { value: u.id, label: `${primary}${suffix}` };
      }),
    [combinedNegotiators],
  );

  const isSyncInProgress = data?.crmSyncStatus === "IN_PROGRESS";
  const isViewingEntry = isDiaryViewingType(data?.type);
  const isAvailabilityEntry = (data?.entryType ?? "").toUpperCase() === "AVAILABILITY";
  const clickedDay =
    occurrenceDate ??
    (data?.startDate ? parseApiDateTime(data.startDate) : null);
  const isPastAvailabilityOccurrence =
    isAvailabilityEntry && clickedDay != null && isBeforeToday(clickedDay);
  const canEditNegotiator =
    Boolean(data) &&
    !isViewingEntry &&
    !data?.isCancelled &&
    !isSyncInProgress &&
    !isPastAvailabilityOccurrence;
  const canEditStatus =
    Boolean(data) &&
    !data?.isCancelled &&
    !isSyncInProgress &&
    !isPastAvailabilityOccurrence;
  const currentStatus = normalizeDiaryStatusValue(data?.status);
  const statusOptions = useMemo(
    () =>
      isViewingEntry
        ? DIARY_STATUS_OPTIONS.filter(
            (option) =>
              option.value === "ACTIVE" ||
              (DIARY_CANCELLED_STATUS_VALUES as readonly string[]).includes(option.value),
          )
        : [...DIARY_STATUS_OPTIONS],
    [isViewingEntry],
  );
  const revertNegotiatorDraft = () => {
    setSelectedNegotiatorId("");
    setEditingNegotiator(false);
  };

  const saveNegotiator = async () => {
    if (!entryId || !data) return;
    if (!canEditNegotiator) {
      toast.info("Viewing diary entries cannot change users here.");
      return;
    }
    if (!selectedNegotiatorId) {
      toast.error("Please select a user.");
      return;
    }
    const selected = combinedNegotiators.find(
      (u) => u.id === selectedNegotiatorId,
    );
    if (!selected) {
      toast.error("Selected user is unavailable. Please try again.");
      return;
    }
    const sourceCrm = (selected.sourceCrm ?? data.sourceCrm ?? "").trim();
    const externalId = (selected.externalId ?? "").trim();
    if (!sourceCrm || !externalId) {
      toast.error("Selected user is missing required identifiers.");
      return;
    }
    const currentUsers = data.users || [];
    if (currentUsers.some((u) => u.id === selected.id)) {
      toast.info("User is already added.");
      setEditingNegotiator(false);
      return;
    }
    setSavingNegotiator(true);
    try {
      await diaryService.update(entryId, {
        users: [
          ...currentUsers.map((u) => ({
            id: u.id,
            sourceCrm: u.sourceCrm,
            externalId: u.externalId,
          })),
          {
            id: selected.id,
            sourceCrm,
            externalId,
          },
        ],
      });
      const fresh = await diaryService.getOne(entryId);
      setData(fresh);
      toast.success("User added.");
      setEditingNegotiator(false);
      onSaved?.(fresh);
    } catch (err) {
      console.error(err);
      showActionErrorFromUnknown(err, "Could not add user. Please try again.");
    } finally {
      setSavingNegotiator(false);
    }
  };

  const removeUser = async (userIdToRemove: string) => {
    if (!entryId || !data) return;
    if (!canEditNegotiator) return;

    setSavingNegotiator(true);
    try {
      const remainingUsers = (data.users || []).filter(u => u.id !== userIdToRemove);
      await diaryService.update(entryId, {
        users: remainingUsers.map(u => ({
          id: u.id,
          sourceCrm: u.sourceCrm,
          externalId: u.externalId,
        })),
      });
      const fresh = await diaryService.getOne(entryId);
      setData(fresh);
      setUserPendingRemoval(null);
      toast.success("User removed.");
      onSaved?.(fresh);
    } catch (err) {
      console.error(err);
      showActionErrorFromUnknown(err, "Could not remove user. Please try again.");
    } finally {
      setSavingNegotiator(false);
    }
  };

  const revertStatusDraft = () => {
    setSelectedStatus(currentStatus);
  };

  const saveStatus = async () => {
    if (!entryId || !data) return;
    if (!canEditStatus) return;
    if (selectedStatus === currentStatus) {
      toast.info("No status change to save.");
      return;
    }
    setSavingStatus(true);
    try {
      await diaryService.update(entryId, {
        status: selectedStatus,
        isCancelled: diaryIsCancelledForStatus(selectedStatus),
      });
      const fresh = await diaryService.getOne(entryId);
      setData(fresh);
      setSelectedStatus(normalizeDiaryStatusValue(fresh.status));
      toast.success("Status updated.");
      onSaved?.(fresh);
    } catch (err) {
      console.error(err);
      showActionErrorFromUnknown(err, "Could not update status. Please try again.");
    } finally {
      setSavingStatus(false);
    }
  };

  // Prefer the detail body so a refetch wins, but it currently omits `syncAlto` —
  // in practice the list row's value is what shows.
  const effectiveCrmSyncStatus = data?.crmSyncStatus ?? data?.syncAlto ?? crmSyncStatus ?? syncAlto;


  const showDiaryNotesTab = supportsDiaryNotesAndFeedback(data?.type);
  const showFeedbackTab = showDiaryNotesTab;
  const detailTabs = useMemo(() => {
    const tabs: ("property" | "feedback" | "notes")[] = ["property"];
    if (showFeedbackTab) tabs.push("feedback");
    if (showDiaryNotesTab) tabs.push("notes");
    return tabs;
  }, [showFeedbackTab, showDiaryNotesTab]);

  useEffect(() => {
    if (!open) return;
    if (!detailTabs.includes(activeTab)) {
      setActiveTab("property");
    }
  }, [open, detailTabs, activeTab]);

  if (!open) return null;

  const title = data?.title ?? "Untitled Entry";
  const internalNotes = data ? diaryInternalNotesFromEntry(data) : null;

  // Heading shows "Viewing: <property address>" only for meeting-category entries with a
  // single linked property (never for VIEWING-type entries); falls back to the entry title.
  const firstProperty = data?.properties?.[0];
  const firstPropertyLabel = firstProperty
    ? propertyDisplayLabel(
        mergeDiaryPropertyStubWithDetail(
          firstProperty,
          (() => {
            const atlasId = diaryLinkedPropertyAtlasId(firstProperty);
            return atlasId ? propertyDetailsById[atlasId] : undefined;
          })(),
        ),
      )
    : null;
  const headingTitle =
    data && isDiaryMeetingEntry(data) && firstPropertyLabel && firstPropertyLabel !== "Unnamed Property"
      ? `Viewing: ${firstPropertyLabel}`
      : title;

  const canEditTimes =
    Boolean(data) &&
    !data?.isCancelled &&
    !isSyncInProgress &&
    !isPastAvailabilityOccurrence;
  const canEditRecurrence =
    Boolean(data) &&
    !isViewingEntry &&
    !data?.isCancelled &&
    !isSyncInProgress &&
    !isPastAvailabilityOccurrence;
  const seriesStartDateKey = data?.startDate ? String(data.startDate).slice(0, 10) : "";
  const getTypeStyles = (type: string | null, title: string | null) => {
    const t = (type || "").toUpperCase();
    const s = (title || "").toUpperCase();

    const isHoliday =
      t === "HOLIDAY" ||
      s.includes("HOLIDAY") ||
      s.includes("LEAVE") ||
      s.includes("OFF");
    const isViewing = t === "VIEWING" || s.includes("VIEWING");
    const isValuation =
      t === "VALUATION" || s.includes("VALUATION") || s.includes("APPRAISAL");
    const isInspection =
      t === "INSPECTION" ||
      s.includes("INSPECTION") ||
      s.includes("SURVEY") ||
      s.includes("REPORT");
    const isMeeting =
      t === "MEETING" || s.includes("MEETING") || s.includes("DISCUSSION");
    const isFollowUp =
      t === "FOLLOW_UP" || s.includes("FOLLOW UP") || s.includes("FOLLOW-UP");

    if (isHoliday) return "bg-rose-500 border-rose-600 text-white font-bold";
    if (isViewing) return "bg-sky-500 border-sky-600 text-white font-semibold";
    if (isValuation)
      return "bg-violet-500 border-violet-600 text-white font-semibold";
    if (isInspection)
      return "bg-amber-500 border-amber-600 text-white font-semibold";
    if (isMeeting)
      return "bg-indigo-500 border-indigo-600 text-white font-semibold";
    if (isFollowUp)
      return "bg-emerald-500 border-emerald-600 text-white font-semibold";

    return "bg-slate-500 border-slate-600 text-white font-medium";
  };

  const revertTimeDraft = () => {
    if (!data?.startDate) {
      setStartLocal("");
      setEndLocal("");
      return;
    }
    if (occurrenceDate && data.recurring) {
      const startDt = parseApiDateTime(data.startDate);
      const endDt = data.endDate ? parseApiDateTime(data.endDate) : startDt;
      if (startDt && endDt) {
        const occStart = new Date(startDt);
        occStart.setFullYear(occurrenceDate.getFullYear(), occurrenceDate.getMonth(), occurrenceDate.getDate());
        const occEnd = new Date(endDt);
        occEnd.setFullYear(occurrenceDate.getFullYear(), occurrenceDate.getMonth(), occurrenceDate.getDate());
        setStartLocal(toDatetimeLocalValue(occStart));
        setEndLocal(toDatetimeLocalValue(occEnd));
        return;
      }
    }
    setStartLocal(toDatetimeLocalValue(data.startDate));
    setEndLocal(
      data.endDate
        ? toDatetimeLocalValue(data.endDate)
        : toDatetimeLocalValue(data.startDate),
    );
  };

  const saveTime = async () => {
    if (!entryId || !data) return;
    const apiStart = fromDatetimeLocalToApi(startLocal);
    const apiEnd = fromDatetimeLocalToApi(endLocal);
    if (!apiStart || !apiEnd) {
      toast.error("Please enter valid start and end times.");
      return;
    }
    const startDt = parseApiDateTime(apiStart);
    const endDt = parseApiDateTime(apiEnd);
    if (!startDt || !endDt || endDt.getTime() <= startDt.getTime()) {
      toast.error("End time must be after start time.");
      return;
    }
    const now = new Date();
    if (startDt.getTime() < now.getTime()) {
      toast.error("Start date cannot be in the past.");
      return;
    }
    if (endDt.getTime() < now.getTime()) {
      toast.error("End date cannot be in the past.");
      return;
    }
    setSavingTime(true);
    try {
      const detailWithEntity = data as DiaryEntryDetail & {
        legalEntityId?: string | null;
      };
      const mergedLegalEntity = detailWithEntity.legalEntityId?.trim()
        ? detailWithEntity.legalEntityId
        : (contextLegalEntityId?.trim() ?? undefined);

      const patch: Record<string, unknown> = {
        startDate: apiStart,
        endDate: apiEnd,
      };
      if (mergedLegalEntity) {
        patch.legalEntityId = mergedLegalEntity;
      }

      await diaryService.update(entryId, patch);
      const fresh = await diaryService.getOne(entryId);
      setData(fresh);
      setStartLocal(toDatetimeLocalValue(fresh.startDate));
      setEndLocal(
        fresh.endDate
          ? toDatetimeLocalValue(fresh.endDate)
          : toDatetimeLocalValue(fresh.startDate),
      );
      toast.success("Times updated.");
      onSaved?.(fresh);
    } catch (err) {
      console.error(err);
      showActionErrorFromUnknown(err, "Could not update times. Please try again.");
    } finally {
      setSavingTime(false);
    }
  };

  const revertRecurrenceDraft = () => {
    const t = (data?.recurrenceType ?? "").trim().toUpperCase();
    setRecurFreq(data?.recurring && (t === "DAILY" || t === "WEEKLY") ? (t as RecurrenceFreq) : "NONE");
    setRecurInterval(Math.max(1, data?.recurrenceInterval ?? 1));
    const todayStr = new Date().toISOString().slice(0, 10);
    const loadedUntil = data?.recurrenceUntil ? String(data.recurrenceUntil).slice(0, 10) : "";
    setRecurUntil(loadedUntil && loadedUntil < todayStr ? "" : loadedUntil);
    setRecurDays(data?.recurrenceDaysOfWeek ?? []);
    setRecurExcluded((data?.recurrenceExceptions ?? []).filter((d) => d >= todayStr));
    setRecurExDraft("");
  };

  const addRecurExcluded = () => {
    if (!recurExDraft) return;
    if (seriesStartDateKey && recurExDraft < seriesStartDateKey) {
      toast.error("Skip date cannot be before the availability start date.");
      return;
    }
    setRecurExcluded((prev) => (prev.includes(recurExDraft) ? prev : [...prev, recurExDraft].sort()));
    setRecurExDraft("");
  };
  const removeRecurExcluded = (d: string) =>
    setRecurExcluded((prev) => prev.filter((x) => x !== d));

  const saveRecurrence = async () => {
    if (!entryId || !data) return;
    if (!canEditRecurrence) {
      toast.info("This entry's recurrence cannot be changed here.");
      return;
    }
    const recurring = recurFreq !== "NONE";
    if (recurring && recurUntil && seriesStartDateKey && recurUntil < seriesStartDateKey) {
      toast.error("Repeat until must be on or after the start date.");
      return;
    }
    if (recurring && recurExcluded.length && seriesStartDateKey) {
      const beforeStart = recurExcluded.find((d) => d < seriesStartDateKey);
      if (beforeStart) {
        toast.error("Skip dates cannot be before the availability start date.");
        return;
      }
    }
    setSavingRecurrence(true);
    try {
      await diaryService.update(entryId, {
        recurring,
        recurrenceType: recurring ? recurFreq : null,
        recurrenceInterval: recurring ? Math.max(1, recurInterval) : null,
        recurrenceUntil: recurring && recurUntil ? `${recurUntil}T23:59:59` : null,
        recurrenceDaysOfWeek: recurring && recurFreq === "WEEKLY" && recurDays.length ? recurDays : null,
        recurrenceExceptions: recurring && recurExcluded.length ? recurExcluded : null,
      });
      const fresh = await diaryService.getOne(entryId);
      setData(fresh);
      toast.success("Recurrence updated.");
      onSaved?.(fresh);
    } catch (err) {
      console.error(err);
      showActionErrorFromUnknown(err, "Could not update recurrence. Please try again.");
    } finally {
      setSavingRecurrence(false);
    }
  };

  const removeUserDialog =
    userPendingRemoval &&
    createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-ink-950/40 p-4 backdrop-blur-sm"
        onClick={onRemoveUserOverlayClick}
      >
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="remove-user-dialog-title"
          className="w-full max-w-md overflow-hidden rounded-xl border border-bridge-border bg-white shadow-premium"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4 border-b border-bridge-border/60 px-6 py-5">
            <div className="flex min-w-0 items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bridge-accent/10 text-bridge-accent">
                <UserMinus size={20} />
              </div>
              <div className="min-w-0">
                <h2 id="remove-user-dialog-title" className="text-lg font-bold text-bridge-fg">
                  Remove user?
                </h2>
                <p className="mt-1 text-sm text-bridge-muted">
                  This user will be unassigned from this diary event.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeRemoveUserDialog}
              disabled={savingNegotiator}
              className="shrink-0 rounded-lg p-1.5 text-bridge-muted transition-colors hover:bg-bridge-surface hover:text-bridge-fg disabled:opacity-40"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-6 py-5">
            <p className="text-sm leading-6 text-bridge-muted">
              Remove{" "}
              <span className="font-semibold text-bridge-fg">{userPendingRemoval.name}</span> from
              this diary event? You can add them again later if needed.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-bridge-border/60 bg-bridge-bg/50 px-6 py-4">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={closeRemoveUserDialog}
              disabled={savingNegotiator}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              loading={savingNegotiator}
              disabled={savingNegotiator}
              onClick={() => void removeUser(userPendingRemoval.id)}
            >
              {savingNegotiator ? "Removing…" : "Yes, remove"}
            </Button>
          </div>
        </section>
      </div>,
      document.body,
    );

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      title={loading ? "Loading..." : (data ? headingTitle : "Diary Entry")}
      subtitle={entryId ? (
        <span className="group/copy inline-flex items-center gap-1">
          <span>ID: {entryId}</span>
          <CopyButton
            value={entryId}
            label="Diary entry ID"
            successMessage="Record ID copied to clipboard"
          />
        </span>
      ) : undefined}
      headerAside={
        data ? (
          <SyncStatus
            crmSyncStatus={effectiveCrmSyncStatus}
            entityType="APPOINTMENT"
            entityId={entryId ?? ""}
            onResynced={onResynced}
          />
        ) : undefined
      }
    >
      {loading && (
        <div className="p-6 text-bridge-muted">Loading entry details...</div>
      )}
      {!loading && loadError && (
        <div className="p-6 text-sm text-red-600">
          <p>{loadError}</p>
          {entryId && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={() => setRetryKey((k) => k + 1)}
            >
              Retry
            </Button>
          )}
        </div>
      )}
      {!loading && !entryId && (
        <div className="p-6 text-sm text-bridge-muted">
          No diary entry selected.
        </div>
      )}
      {!loading && data && (
        <div className="flex flex-col h-full bg-bridge-surface overflow-auto">
          {data?.crmSyncStatus === "FAILED" && data?.crmSyncError && (
            <div className="m-6 mb-0 p-4 rounded-md bg-red-50 border border-red-200 shrink-0">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-red-800">CRM Sync Failed</h3>
                  <p className="mt-1 text-sm text-red-700">{friendlyCrmSyncError(data.crmSyncError)}</p>
                </div>
              </div>
            </div>
          )}
          {/* Header Section mimicking the screenshot top bar */}
          <div className="shrink-0 p-6 border-b border-bridge-border/60 bg-bridge-surface-raised flex flex-col gap-4">
            <div className="flex justify-between items-start gap-4">
              <h2 className="text-xl font-semibold text-bridge-fg min-w-0 flex-1">
                {headingTitle}
              </h2>
              <div className="flex flex-wrap justify-end gap-2 shrink-0 items-center">
                {isSyncInProgress && (
                   <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                     <span className="relative flex h-2 w-2">
                       <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                       <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                     </span>
                     {crmDisplayName(data.sourceCrm)} Sync In Progress
                   </span>
                )}
                {data.isCancelled ? (
                  <Badge tone="danger">Cancelled</Badge>
                ) : (
                  <Badge tone="accent">{currentStatus}</Badge>
                )}
                {data.type && (
                  <Badge className={getTypeStyles(data.type, data.title)}>
                    {data.type}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 rounded-md bg-bridge-surface px-2.5 py-1.5 text-sm text-bridge-fg w-fit">
              <History size={14} className="text-bridge-muted shrink-0" />
              <span>
                Created by <span className="font-medium">{data.atlasCreatedBy || "Unknown"}</span>
                <span className="text-bridge-muted"> · Last modified by </span>
                <span className="font-medium">{data.atlasModifiedBy || "Unknown"}</span>
                {data.atlasModifiedAt && (
                  <span className="text-bridge-muted"> ({formatRelativeTime(data.atlasModifiedAt)})</span>
                )}
              </span>
            </div>

            {canEditStatus ? (
              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-bridge-muted">Status</label>
                <MenuSelect
                  hideLabel
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  options={statusOptions}
                  placeholder="Select status"
                  disabled={savingStatus || savingTime}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    loading={savingStatus}
                    disabled={savingTime}
                    onClick={saveStatus}
                  >
                    Save status
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={savingStatus || savingTime}
                    onClick={revertStatusDraft}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}

            {data.startDate ? (
              <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-4 w-full">
                  <div className="min-w-0 flex-1">
                    <DiaryDateTimeField
                      id="diary-entry-start"
                      label="Start"
                      value={startLocal}
                      onChange={setStartLocal}
                      disabled={!canEditTimes || savingTime || savingStatus}
                      min={todayDatetimeLocalMin()}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <DiaryDateTimeField
                      id="diary-entry-end"
                      label="End"
                      value={endLocal}
                      onChange={setEndLocal}
                      disabled={!canEditTimes || savingTime || savingStatus}
                      min={todayDatetimeLocalMin()}
                    />
                  </div>
                </div>
                {!canEditTimes ? (
                  <p className="text-xs text-bridge-muted">
                    {data.isCancelled
                      ? "Cancelled entries cannot be rescheduled."
                      : isPastAvailabilityOccurrence
                        ? "Past availability is view-only."
                        : "This entry cannot be rescheduled while an Alto sync is in progress."}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      loading={savingTime}
                      disabled={savingStatus}
                      onClick={saveTime}
                    >
                      Save Changes
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={savingTime || savingStatus}
                      onClick={revertTimeDraft}
                    >
                      Reset
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-bridge-muted">
                No start time on this entry.
              </p>
            )}

            {(canEditRecurrence || recurFreq !== "NONE") && (
              <div className="flex flex-col gap-2 w-full border-t border-bridge-border/30 pt-3">
                <label className="text-xs font-medium text-bridge-muted">Recurrence</label>
                {canEditRecurrence ? (
                  <>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                      <div className="min-w-0 flex-1">
                        <MenuSelect
                          hideLabel
                          value={recurFreq}
                          onChange={(v) => setRecurFreq(v as RecurrenceFreq)}
                          options={[...RECURRENCE_OPTIONS]}
                          disabled={savingRecurrence}
                        />
                      </div>
                      {recurFreq !== "NONE" && (
                        <div className="w-28">
                          <label className="text-[11px] text-bridge-muted block mb-1">Repeat Every</label>
                          <Input
                            type="number"
                            min={1}
                            value={recurInterval}
                            onChange={(e) => setRecurInterval(Number(e.target.value) || 1)}
                            disabled={savingRecurrence}
                          />
                        </div>
                      )}
                      {recurFreq !== "NONE" && (
                        <div className="min-w-0 flex-1">
                          <label className="text-[11px] text-bridge-muted block mb-1">Repeat until</label>
                          <Input
                            type="date"
                            value={recurUntil}
                            min={seriesStartDateKey || new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setRecurUntil(e.target.value)}
                            disabled={savingRecurrence}
                          />
                        </div>
                      )}
                    </div>
                    {recurFreq === "WEEKLY" && (
                      <div>
                        <label className="text-[11px] text-bridge-muted block mb-1">Repeat on</label>
                        <WeekdayPicker value={recurDays} onChange={setRecurDays} disabled={savingRecurrence} />
                        <p className="mt-1 text-[11px] text-bridge-subtle">
                          Pick days (e.g. Mon–Fri to skip weekends). Empty = the start day's weekday.
                        </p>
                      </div>
                    )}
                    {recurFreq !== "NONE" && (
                      <div>
                        <label className="text-[11px] text-bridge-muted block mb-1">
                          Skip days (exclude these dates)
                        </label>
                        <div className="flex items-end gap-2">
                          <Input
                            type="date"
                            value={recurExDraft}
                            min={seriesStartDateKey || new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setRecurExDraft(e.target.value)}
                            disabled={savingRecurrence}
                          />
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={addRecurExcluded}
                            disabled={!recurExDraft || savingRecurrence}
                          >
                            Add
                          </Button>
                        </div>
                        {recurExcluded.length > 0 && (
                          <ul className="mt-2 flex flex-wrap gap-2">
                            {recurExcluded.map((d) => (
                              <li
                                key={d}
                                className="flex items-center gap-1 rounded-full border border-bridge-border/60 bg-bridge-surface px-2 py-0.5 text-xs"
                              >
                                {d}
                                <button
                                  type="button"
                                  className="text-bridge-muted hover:text-rose-500"
                                  onClick={() => removeRecurExcluded(d)}
                                  aria-label={`Remove ${d}`}
                                >
                                  <X size={12} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        loading={savingRecurrence}
                        disabled={savingTime || savingStatus}
                        onClick={saveRecurrence}
                      >
                        Save recurrence
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={savingRecurrence}
                        onClick={revertRecurrenceDraft}
                      >
                        Reset
                      </Button>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-bridge-fg">
                    {recurFreq === "WEEKLY" ? "Weekly" : "Daily"}
                    {recurInterval > 1 ? ` (every ${recurInterval})` : ""}
                    {recurUntil ? ` until ${recurUntil}` : ""}
                  </p>
                )}
              </div>
            )}
            {/* <div className="flex gap-4 text-sm text-bridge-fg/80 border-t border-bridge-border/30 pt-3">
              <div className="flex items-center gap-1.5">
                <UserCheck size={16} className="text-bridge-accent" />
                {data.accompanied ? "Accompanied" : "Unaccompanied"}
              </div>
              {data.virtual && (
                <div className="flex items-center gap-1.5 text-blue-500">
                  <MapPin size={16} /> Virtual Viewing
                </div>
              )}
            </div> */}

            {/* <div className="flex gap-2 mt-2">
              <button className="flex-1 py-1.5 px-3 bg-bridge-accent text-white text-sm font-medium rounded hover:bg-bridge-accent/90 transition-colors">
                Book Repeat
              </button>
              <button className="flex-1 py-1.5 px-3 bg-bridge-surface border border-bridge-border/60 text-bridge-fg text-sm font-medium rounded hover:bg-bridge-surface-raised transition-colors">
                Make Offer
              </button>
            </div> */}
          </div>

          {/* Navigation Tabs */}
          <div className="shrink-0 flex border-b border-bridge-border/60 px-6 mt-4 gap-6">
            {detailTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={clsx(
                  "pb-2 text-sm font-medium border-b-2 transition-colors",
                  activeTab === tab
                    ? "border-bridge-accent text-bridge-fg"
                    : "border-transparent text-bridge-muted hover:text-bridge-fg",
                )}
              >
                {tab === "property"
                  ? "Property & People"
                  : tab === "feedback"
                    ? "Viewers Feedback"
                    : "Diary Notes"}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 p-6">
            {activeTab === "property" && (
              <div className="flex flex-col gap-6">
                <Section title="The Property" icon={<MapPin size={16} />}>
                  {data.properties?.length ? (
                    <>
                      {propertyDetailsLoading && (
                        <p className="text-xs text-bridge-muted mb-2">
                          Loading property details…
                        </p>
                      )}
                      <ul className="flex flex-col gap-3">
                        {data.properties.map((p, idx) => {
                          const atlasId = diaryLinkedPropertyAtlasId(p);
                          const rowKey =
                            atlasId ?? p.externalId ?? `property-${idx}`;
                          const merged = mergeDiaryPropertyStubWithDetail(
                            p,
                            atlasId ? propertyDetailsById[atlasId] : undefined,
                          );
                          const blurb = propertyNarrativeText(merged);
                          const meta = [
                            merged.market,
                            merged.propertyType,
                            merged.availabilityStatus,
                          ].filter(Boolean);
                          return (
                            <li
                              key={rowKey}
                              className="text-sm text-bridge-fg bg-white p-4 rounded-lg border border-bridge-border/40 shadow-sm"
                            >
                              <div className="flex items-start gap-3">
                                <Home
                                  className="text-bridge-accent mt-0.5"
                                  size={16}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-base">
                                    {propertyDisplayLabel(merged)}
                                  </div>
                                  <div className="text-xs text-bridge-muted mt-1 uppercase tracking-wider font-bold">
                                    {meta.length ? meta.join(" • ") : "—"}
                                  </div>
                                  {merged.postcode ? (
                                    <div className="text-xs text-bridge-muted mt-0.5 font-medium">
                                      {merged.postcode}
                                    </div>
                                  ) : null}
                                  {merged.priceQualifier ? (
                                    <div className="text-xs text-bridge-muted mt-1">
                                      {merged.priceQualifier}
                                    </div>
                                  ) : null}
                                  <div className="flex flex-wrap gap-4 mt-3 text-xs text-bridge-fg/80 bg-bridge-surface px-3 py-2 rounded-md">
                                    <div className="flex items-center gap-1">
                                      <span className="font-bold">
                                        {merged.bedrooms ?? 0}
                                      </span>{" "}
                                      Bed
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span className="font-bold">
                                        {merged.bathrooms ?? 0}
                                      </span>{" "}
                                      Bath
                                    </div>
                                    {merged.receptions != null && (
                                      <div className="flex items-center gap-1">
                                        <span className="font-bold">
                                          {merged.receptions}
                                        </span>{" "}
                                        Rec
                                      </div>
                                    )}
                                    <div className="flex items-center gap-1 font-bold text-bridge-accent">
                                      {merged.currency ?? ""}
                                      {merged.price != null
                                        ? merged.price.toLocaleString()
                                        : "—"}
                                    </div>
                                  </div>
                                  {blurb ? (
                                    <p className="text-xs text-bridge-fg/80 mt-3 line-clamp-5 whitespace-pre-line">
                                      {blurb}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : (
                    <p className="text-sm text-bridge-muted italic">
                      No property linked.
                    </p>
                  )}
                </Section>

                <Section title="Applicant(s)" icon={<Users size={16} />}>
                  {contactDetailsLoading && data.contacts?.length ? (
                    <p className="text-xs text-bridge-muted mb-2">
                      Loading applicant details…
                    </p>
                  ) : null}
                  {data.contacts?.length ? (
                    <ul className="flex flex-col gap-3">
                      {data.contacts.map((stub: ContactSummary, idx) => {
                        const atlasContactId =
                          typeof stub.id === "string" &&
                          stub.id.trim().length > 0
                            ? stub.id.trim()
                            : "";
                        const c = mergeDiaryContactStubWithSummary(
                          stub,
                          atlasContactId
                            ? contactSummariesById[atlasContactId]
                            : undefined,
                        );
                        const displayName =
                          c.name?.trim() ||
                          (c.contactPerson && String(c.contactPerson).trim()) ||
                          (c.externalId
                            ? `Alto ref ${String(c.externalId)}`
                            : null) ||
                          "Unnamed Contact";
                        const rowKey =
                          atlasContactId ||
                          (c.externalId != null
                            ? String(c.externalId)
                            : `contact-${idx}`);
                        return (
                          <li
                            key={rowKey}
                            className="text-sm text-bridge-fg bg-white p-4 rounded-lg border border-bridge-border/40 shadow-sm"
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <User className="text-bridge-info" size={16} />
                              <div className="font-semibold text-base">
                                {displayName}
                              </div>
                              {c.category ? (
                                <Badge tone="neutral">{c.category}</Badge>
                              ) : null}
                            </div>
                            <div className="flex flex-col gap-1.5 pl-7">
                              {c.email ? (
                                <div className="flex items-center gap-2 text-xs text-bridge-muted">
                                  <Mail size={12} /> {c.email}
                                </div>
                              ) : null}
                              {c.mobilePhone ? (
                                <div className="flex items-center gap-2 text-xs text-bridge-muted">
                                  <Phone size={12} /> {c.mobilePhone}
                                </div>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-bridge-muted italic">
                      No applicants linked.
                    </p>
                  )}
                </Section>

                <Section
                  title="User(s)"
                  icon={<UserCircle size={16} />}
                  actions={
                    canEditNegotiator ? (
                      <IconButton
                        type="button"
                        label={
                          editingNegotiator
                            ? "Close user edit"
                            : "Add user"
                        }
                        onClick={() => {
                          if (editingNegotiator) {
                            revertNegotiatorDraft();
                            return;
                          }
                          setEditingNegotiator(true);
                        }}
                      >
                        <UserPlus size={16} />
                      </IconButton>
                    ) : null
                  }
                >
                  {editingNegotiator ? (
                    <div className="mb-3 rounded-lg border border-bridge-border/60 bg-bridge-surface-raised p-3">
                      <div className="text-xs text-bridge-muted mb-2">
                        Select a user to add to this diary entry.
                      </div>
                      <MenuSelect
                        label="User"
                        hideLabel
                        value={selectedNegotiatorId}
                        onChange={setSelectedNegotiatorId}
                        options={negotiatorOptions}
                        placeholder={
                          negotiatorOptions.length
                            ? "Select a negotiator"
                            : loadingNegotiators
                              ? "Loading negotiators…"
                              : "Open and search negotiators"
                        }
                        searchable
                        searchInTrigger
                        searchPlaceholder="Search negotiators…"
                        searchDebounceMs={300}
                        onDropdownSearchQueryChange={fetchNegotiators}
                        disabled={savingNegotiator}
                      />
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="primary"
                          loading={savingNegotiator}
                          onClick={saveNegotiator}
                        >
                          Add user
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={savingNegotiator}
                          onClick={revertNegotiatorDraft}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  {userDetailsLoading && data.users?.length ? (
                    <p className="text-xs text-bridge-muted mb-2">
                      Loading user details…
                    </p>
                  ) : null}
                  {data.users?.length ? (
                    <ul className="flex flex-col gap-3">
                      {data.users.map((stub: UserSummary, idx) => {
                        const atlasUserId =
                          typeof stub.id === "string" &&
                          stub.id.trim().length > 0
                            ? stub.id.trim()
                            : "";
                        const u = mergeDiaryUserStubWithSummary(
                          stub,
                          atlasUserId
                            ? userSummariesById[atlasUserId]
                            : undefined,
                        );
                        const displayName =
                          u.name?.trim() ||
                          (u.email && u.email.trim()) ||
                          (u.externalId
                            ? `User ${String(u.externalId)}`
                            : null) ||
                          "Unnamed User";
                        const rowKey =
                          atlasUserId ||
                          (u.externalId != null
                            ? String(u.externalId)
                            : `user-${idx}`);
                        return (
                          <li
                            key={rowKey}
                            className="text-sm text-bridge-fg bg-white p-4 rounded-lg border border-bridge-border/40 shadow-sm"
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <UserCircle
                                className="text-bridge-accent"
                                size={18}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-base truncate">
                                  {displayName}
                                </div>
                                <div className="text-xs text-bridge-muted truncate">
                                  {u.role ? u.role.replace(/_/g, " ") : "User"}
                                </div>
                              </div>
                              {canEditNegotiator && (
                                <IconButton
                                  type="button"
                                  label="Remove user"
                                  disabled={savingNegotiator}
                                  onClick={() =>
                                    setUserPendingRemoval({
                                      id: stub.id as string,
                                      name: displayName,
                                    })
                                  }
                                >
                                  <X size={16} className="text-bridge-muted hover:text-rose-500" />
                                </IconButton>
                              )}
                            </div>
                            {u.email ? (
                              <div className="flex items-center gap-2 text-xs text-bridge-muted pl-7">
                                <Mail size={12} /> {u.email}
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-bridge-muted italic">
                      No users assigned.
                    </p>
                  )}
                </Section>
              </div>
            )}

            {activeTab === "feedback" && showFeedbackTab && (
              <div className="flex flex-col gap-4">
                <Section
                  title="Applicant Feedback"
                  icon={<AlignLeft size={16} />}
                >
                  <div className="bg-bridge-surface-raised p-4 rounded-lg border border-bridge-border/40 min-h-[150px] whitespace-pre-wrap text-sm text-bridge-fg">
                    {data.viewingDetailsApplicantFeedback || (
                      <span className="text-bridge-muted italic">
                        No feedback provided yet.
                      </span>
                    )}
                  </div>
                </Section>
              </div>
            )}

            {activeTab === "notes" && showDiaryNotesTab && (
              <div className="flex flex-col gap-4">
                <Section title="Diary Notes" icon={<AlignLeft size={16} />}>
                  <div className="bg-bridge-surface-raised p-4 rounded-lg border border-bridge-border/40 min-h-[150px] whitespace-pre-wrap text-sm text-bridge-fg">
                    {internalNotes || (
                      <span className="text-bridge-muted italic">
                        No internal notes available.
                      </span>
                    )}
                  </div>
                </Section>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
    {removeUserDialog}
    </>
  );
}

function Section({
  title,
  icon,
  actions,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-bridge-fg flex items-center gap-2">
          <span className="text-bridge-muted">{icon}</span> {title}
        </h3>
        {actions ? <div className="ml-auto">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

function mergeDiaryContactStubWithSummary(
  stub: ContactSummary,
  detailed?: ContactSummary,
): ContactSummary {
  if (!detailed) return stub;
  return { ...stub, ...detailed, id: stub.id };
}

function mergeDiaryUserStubWithSummary(
  stub: UserSummary,
  detailed?: UserSummary,
): UserSummary {
  if (!detailed) return stub;
  return { ...stub, ...detailed, id: stub.id };
}

function mergeDiaryPropertyStubWithDetail(
  stub: PropertySummary,
  detail: PropertyDetail | undefined,
): PropertySummary & Partial<PropertyDetail> {
  return detail ? { ...stub, ...detail } : stub;
}

/** Diary payloads may link via `propertyRefId` (create) without setting `id` on the stub. */
function diaryLinkedPropertyAtlasId(
  p: PropertySummary & { propertyRefId?: string | null },
): string | null {
  const a = typeof p.id === "string" && p.id.trim() ? p.id.trim() : "";
  const ref = p as PropertySummary & {
    propertyRefId?: string | null;
    property_ref_id?: string | null;
  };
  const b =
    (typeof ref.propertyRefId === "string" && ref.propertyRefId.trim()
      ? ref.propertyRefId.trim()
      : "") ||
    (typeof ref.property_ref_id === "string" && ref.property_ref_id.trim()
      ? ref.property_ref_id.trim()
      : "");
  return a || b || null;
}

function propertyNarrativeText(
  p: PropertySummary & Partial<PropertyDetail>,
): string | null {
  const top =
    typeof p.summary === "string" && p.summary.trim() ? p.summary.trim() : "";
  if (top) return top;
  const nested = p.description?.description;
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  return null;
}
